import React from 'react';
import { Formik, FormikProps } from 'formik';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { generateParameterSystemName } from 'Utils';
import useIsLeavingForm from 'Hooks/useIsLeavingForm';
import Header from 'Routes/Parameters/Pages/CreateExpression/Header';
import useAlertBanner from 'Hooks/reduxHelperHooks/useAlertBanner';
import useReduxModal from 'Hooks/reduxHelperHooks/useReduxModal';
import usePatchExpressionParameter from 'Hooks/query-hooks/parameters/usePatchExpressionParameter';
import InfoOverview from 'Routes/Parameters/components/InfoOverview';
import {
    genParameterInfoOverviewBottom,
    genParameterInfoOverviewTop
} from 'Components/CreateExpressionForm/CreateExpressionForm';
import ScriptOverview from 'Components/ScriptOverview';
import { addParameterValidationSchema } from 'Components/CreateExpressionForm/createExpressionSchema';
import { ROUTES } from 'Components/Breadcrumbs/routes';

type EditExpressionFormProps = {
    initialValues: PatchExpParamFormSchema;
    parameterData?: UseGetParametersResponse;
};

const EditExpressionForm: React.FC<EditExpressionFormProps> = ({ initialValues, parameterData }) => {
    const formRef = React.useRef<FormikProps<PatchExpParamFormSchema>>(null);
    const { addAlert } = useAlertBanner();
    const { addModal } = useReduxModal();
    const { t } = useTranslation();
    const { push } = useHistory();

    useIsLeavingForm(formRef);

    const handleCancel = () => {
        push(ROUTES.PARAMETERS.INDEX);
    };

    const onSuccess = (_data: any, variables: PatchParameterRequest) => {
        addAlert({
            id: 'update-expression-parameter',
            content: t('CreateExpressionParameterSuccessModalText', {
                displayName: variables.message,
                status: variables.status
            }),
            status: 'success'
        });
        push(ROUTES.PARAMETERS.INDEX);
    };

    const onError = (_err: any, variables: PatchParameterRequest) => {
        addModal({
            id: 'update-expression-parameter-error',
            status: 'error',
            title: t('Parameter could not be saved'),
            content: t('CreateExpressionParameterErrorModalText', { displayName: variables.fieldName }),
            cancelText: t('Close')
        });
        formRef.current?.resetForm({
            // retain user's values
            values: {
                ...formRef.current.values,
                isScriptValidated: false,
                revalidateScript: true
            },
            touched: formRef.current.touched,
            errors: formRef.current.errors,
            isSubmitting: false
        });
    };

    const mutationOptions = { onSuccess, onError };
    const mutation = usePatchExpressionParameter({ mutationOptions });

    const handleFormSubmit = (formValues: PatchExpParamFormSchema) => {
        const formattedSystemName = generateParameterSystemName(formValues.displayName);
        const { displayName, expression, description, id, status, tppType } = formValues;

        const payload: PatchParameterRequest = {
            description,
            expressionStatement: expression.expText,
            fieldName: formattedSystemName,
            id,
            message: displayName,
            status,
            tppType
        };
        mutation.mutate(payload);
    };

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={addParameterValidationSchema(t)}
            onSubmit={handleFormSubmit}
            innerRef={formRef}
        >
            {(formikProps) => {
                const { handleChange, handleBlur, isSubmitting, values, setFieldValue } = formikProps;
                const paramInfoOverviewTopData = genParameterInfoOverviewTop(
                    t,
                    isSubmitting,
                    values,
                    handleChange,
                    handleBlur,
                    setFieldValue
                );
                const paramInfoOverviewBottomData = genParameterInfoOverviewBottom(
                    t,
                    isSubmitting,
                    handleChange,
                    handleBlur
                );

                return (
                    <>
                        <Header handleCancel={handleCancel} mode='edit' />
                        <InfoOverview
                            dataTestId='parameterInformation'
                            leftTitle={t('Parameter Information')}
                            topData={paramInfoOverviewTopData}
                            bottomData={paramInfoOverviewBottomData}
                        />

                        <ScriptOverview
                            scriptType='parameter'
                            inputFields={[]}
                            parameterData={parameterData}
                            leftTitle={t('Parameter Statement')}
                            readOnly={false}
                        />
                    </>
                );
            }}
        </Formik>
    );
};

export default EditExpressionForm;
